import React, { useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  ArrowLeft, Mail, Loader2, Trash2, CheckCircle2, Circle, Paperclip,
} from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";

export default function AdminInbox() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/messages");
      setMessages(r.data);
      setError("");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const unread = useMemo(() => messages.filter((m) => !m.read).length, [messages]);

  const markRead = async (id) => {
    try {
      await api.put(`/admin/messages/${id}/read`);
      setMessages((prev) =>
        prev.map((m) => (m.id === id ? { ...m, read: true } : m))
      );
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this message? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/messages/${id}`);
      setMessages((prev) => prev.filter((m) => m.id !== id));
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div
      className="admin-chrome min-h-screen bg-[#F8FAFC] text-[var(--brand-ink)]"
      data-testid="page-admin-inbox"
    >
      <header className="border-b border-black/10 bg-white">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-5 py-4 sm:px-8">
          <RouterLink
            to="/admin"
            className="inline-flex items-center gap-2 font-display text-sm font-semibold"
          >
            <ArrowLeft size={14} /> Admin
          </RouterLink>
          <span className="label-kicker text-[var(--brand-body)]">Inbox</span>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-5 py-12 sm:px-8">
        <div className="mb-8 border-b border-black/10 pb-6">
          <div className="flex items-center gap-2 text-[#6CABDD]">
            <Mail size={16} strokeWidth={2.5} />
            <span className="label-kicker">Contact submissions</span>
          </div>
          <div className="mt-2 flex items-end justify-between gap-4">
            <h1 className="font-display text-3xl font-black tracking-tight sm:text-4xl">
              Fan mail, tips and takedowns.
            </h1>
            <span
              className="rounded-full border border-black/10 bg-white px-3 py-1.5 label-kicker text-[var(--brand-ink)]"
              data-testid="inbox-unread"
            >
              {unread} unread
            </span>
          </div>
        </div>

        {error ? (
          <p
            className="mb-4 rounded-sm border-l-2 border-[#E63946] bg-[#E63946]/5 px-3 py-2 text-sm"
            data-testid="inbox-error"
          >
            {error}
          </p>
        ) : null}

        {loading ? (
          <div className="flex items-center gap-2 text-[var(--brand-body)]">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </div>
        ) : messages.length === 0 ? (
          <div
            className="rounded-md border border-dashed border-black/15 bg-white p-12 text-center"
            data-testid="inbox-empty"
          >
            <p className="font-display text-lg font-bold">Inbox zero.</p>
            <p className="mt-1 text-sm text-[var(--brand-body)]">
              No messages right now. When fans use the contact form they'll
              appear here.
            </p>
          </div>
        ) : (
          <ul className="space-y-4" data-testid="inbox-list">
            {messages.map((m) => (
              <li
                key={m.id}
                data-testid="inbox-row"
                className={`overflow-hidden rounded-md border bg-white ${
                  m.read ? "border-black/10" : "border-[#6CABDD]"
                }`}
              >
                <div className="flex flex-col gap-3 border-b border-black/5 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="label-kicker text-[#6CABDD]">
                        {new Date(m.created_at).toLocaleString("en-GB")}
                      </span>
                      {m.kind === "merch_enquiry" ? (
                        <span
                          className="rounded-sm bg-amber-100 px-1.5 py-0.5 label-kicker text-amber-800"
                          data-testid="inbox-merch-enquiry-badge"
                        >
                          Merch enquiry
                        </span>
                      ) : null}
                      {!m.read ? (
                        <span
                          className="rounded-sm bg-[#6CABDD] px-1.5 py-0.5 label-kicker text-white"
                          data-testid="inbox-new-badge"
                        >
                          New
                        </span>
                      ) : null}
                    </div>
                    <h3 className="mt-1 font-display text-lg font-bold">
                      {m.subject || "(no subject)"}
                    </h3>
                    <p className="label-kicker text-[var(--brand-body)]">
                      From {m.name} ·{" "}
                      <a
                        href={`mailto:${m.email}?subject=Re: ${encodeURIComponent(m.subject || "your message to LAST")}`}
                        className="underline decoration-[#6CABDD] underline-offset-4"
                      >
                        {m.email}
                      </a>
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {m.read ? (
                      <span className="inline-flex items-center gap-1.5 label-kicker text-[var(--brand-body)]">
                        <CheckCircle2 size={12} /> Read
                      </span>
                    ) : (
                      <button
                        onClick={() => markRead(m.id)}
                        data-testid="inbox-mark-read"
                        className="inline-flex items-center gap-1.5 rounded-md border border-black/10 bg-white px-3 py-1.5 label-kicker hover:border-[#0A0F17]"
                      >
                        <Circle size={12} /> Mark read
                      </button>
                    )}
                    <button
                      onClick={() => remove(m.id)}
                      data-testid="inbox-delete"
                      className="inline-flex items-center gap-1.5 rounded-md border border-[#E63946] px-3 py-1.5 label-kicker text-[#E63946] hover:bg-[#E63946] hover:text-white"
                    >
                      <Trash2 size={12} /> Delete
                    </button>
                  </div>
                </div>
                <div className="whitespace-pre-wrap px-5 py-4 text-sm leading-relaxed text-[var(--brand-ink)]">
                  {m.message}
                </div>
                {m.attachments && m.attachments.length ? (
                  <div
                    data-testid="inbox-attachments"
                    className="flex flex-wrap items-center gap-2 border-t border-dashed border-black/10 bg-[#F8FAFC] px-5 py-3 text-xs text-[var(--brand-body)]"
                  >
                    <Paperclip size={12} strokeWidth={2.5} />
                    <span className="label-kicker">
                      {m.attachments.length}{" "}
                      attachment{m.attachments.length === 1 ? "" : "s"} —
                      check the notification email
                    </span>
                    <span className="text-[var(--brand-body)]/70">·</span>
                    <span>
                      {m.attachments.map((a) => a.filename).join(", ")}
                    </span>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}
